import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  PropsWithChildren,
} from 'react';
import organizationsService from '@/services/organizations';
import { Organization } from '@/types/api/organization';

const ORG_STORAGE_KEY = 'zion_active_org_id';
const TRIO_ORG_ID = '00000000-0000-0000-0000-000000000000';

type OrganizationContextType = {
  organizations: Organization[];
  activeOrg: Organization | null;
  activeOrgId: string | null;
  isLoading: boolean;
  switchOrg: (orgId: string) => void;
  refetchOrgs: () => Promise<void>;
};

const OrganizationContext = createContext<OrganizationContextType>({
  organizations: [],
  activeOrg: null,
  activeOrgId: null,
  isLoading: true,
  switchOrg: () => {},
  refetchOrgs: async () => {},
});

export const useOrganization = (): OrganizationContextType => useContext(OrganizationContext);

export const OrganizationProvider = function ({ children }: PropsWithChildren): JSX.Element {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(TRIO_ORG_ID);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrgs = useCallback(async () => {
    try {
      const orgs = await organizationsService.getMyOrganizations();
      setOrganizations(orgs);

      // Force Trio org for now — org switching is disabled
      setActiveOrgId(TRIO_ORG_ID);
      localStorage.setItem(ORG_STORAGE_KEY, TRIO_ORG_ID);
    } catch {
      // Not authenticated yet or no orgs
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // Note: RLS uses current_org_id() which falls back to the user's first org membership.
  // For explicit org switching, queries should filter by organization_id directly.

  const switchOrg = useCallback((_orgId: string) => {
    // Org switching disabled — always use Trio
  }, []);

  const activeOrg = useMemo(
    () => organizations.find((o) => o.id === activeOrgId) ?? null,
    [organizations, activeOrgId]
  );

  const value = useMemo(
    () => ({ organizations, activeOrg, activeOrgId, isLoading, switchOrg, refetchOrgs: fetchOrgs }),
    [organizations, activeOrg, activeOrgId, isLoading, switchOrg, fetchOrgs]
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};
